import { useToast } from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CopiedSourcingEventDto, useCopySourcingEventV2Mutation } from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { urls } from "../../urls";

export const useCopyProcurement = ({ onError }: { onError?: (e: unknown) => void } = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { trigger } = useApiError();
  const [copyEvent, { isLoading }] = useCopySourcingEventV2Mutation();
  const reportError = onError ?? trigger;

  const copy = async (eventId: string) => {
    try {
      const copied = await copyEvent({ eventId }).unwrap();
      navigate(urls.v2.sourcingEvents.edit.go(copied.event.id).info);
      const warning = invitationWarning(copied, t);
      if (warning) toast({ status: "warning", duration: null, isClosable: true, ...warning });
    } catch (e) {
      reportError(e);
    }
  };

  return { copy, isCopying: isLoading };
};

const invitationWarning = (
  copied: CopiedSourcingEventDto,
  t: TFunction,
): { title: string; description: string } | null => {
  if (copied.copiedInvitations === 0 && copied.skippedInvitations === 0) return null;
  const lines = [
    copied.copiedInvitations > 0
      ? t("The invitations from the original were copied. Review them before you publish.")
      : null,
    copied.skippedInvitations > 0
      ? t("{{count}} invitations without an organization were left out.", { count: copied.skippedInvitations })
      : null,
  ].filter((line): line is string => line != null);
  const title = copied.copiedInvitations > 0 ? t("The copy carries invitations") : t("Invitations were left out");
  return { title: String(title), description: lines.join(" ") };
};
